import { createContext, ReactNode, useCallback, useState } from 'react';

export type NotificationType = 'success' | 'info' | 'error' | 'confetti';
export type NotificationPosition = 'top' | 'bottom' | 'center' | 'below-header';

export interface Notification {
  id: string;
  message: ReactNode; // Support React nodes for rich content
  type: NotificationType;
  position?: NotificationPosition;
  duration?: number; // in milliseconds, 0 = no auto-dismiss
  leftContent?: ReactNode; // Optional left-side content (e.g., icon, image)
  metadata?: Record<string, unknown>; // For additional data (e.g., tradeType, confetti params)
}

interface NotificationContextValue {
  notifications: Notification[];
  showNotification: (notification: Omit<Notification, 'id'>) => string; // Returns notification ID
  dismissNotification: (id: string) => void;
  clearAllNotifications: () => void;
}

export const NotificationContext =
  createContext<NotificationContextValue | null>(null);

interface NotificationProviderProps {
  children: ReactNode;
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const showNotification = useCallback(
    (notification: Omit<Notification, 'id'>): string => {
      const id = `notification_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

      const newNotification: Notification = {
        ...notification,
        id,
        position: notification.position ?? 'top',
        duration: notification.duration ?? 3000,
      };

      setNotifications(prev => [...prev, newNotification]);

      // Auto-dismiss if duration is set
      if (newNotification.duration && newNotification.duration > 0) {
        setTimeout(() => {
          dismissNotification(id);
        }, newNotification.duration);
      }

      return id;
    },
    [],
  );

  const dismissNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        showNotification,
        dismissNotification,
        clearAllNotifications,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}
